import React, { useEffect, useState } from "react";
import "@/App.css";
import {
    Flame,
    Wrench,
    Waypoints,
    Layers,
    Boxes,
    Phone,
    Mail,
    MapPin,
    ArrowRight,
    ShieldCheck,
    Cog,
    Award,
    Users,
    HardHat,
    Menu,
    X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---- Business constants (easy to update) ----
const BRAND = {
    name: "Jesha Engineering Company",
    tagline: "Boiler Contractors. Heavy Fabrication. Since 25+ Years",
    logo: "https://customer-assets-lxgj4vgw.emergentagent.net/job_web-forge-1752/artifacts/xxjtgb0b_WhatsApp%20Image%202026-07-21%20at%2013.25.11.jpeg",
    phone: "+91 98855 08810",
    phoneRaw: "919885508810",
    email: "jeshaeng.co@yahoo.com",
    address: "Nellore, Andhra Pradesh, India",
    license: "AP Boiler Licence Reg. No. 84/2008",
};

const SERVICES = [
    {
        icon: Flame,
        title: "Boiler Installation",
        desc: "AP boiler licence holders providing turnkey installation of industrial steam boilers with full statutory compliance.",
    },
    {
        icon: Wrench,
        title: "Boiler Repairs & Overhaul",
        desc: "Preventive maintenance, tube replacement, refractory works and complete overhauling by certified welders.",
    },
    {
        icon: Waypoints,
        title: "Steam & Utility Pipelines",
        desc: "High-pressure steam, condensate, water, compressed air and gas utility piping. Schedule 40/80 lines with insulation and pressure testing.",
    },
    {
        icon: Layers,
        title: "Sheet Metal Fabrication",
        desc: "Precision cutting, rolling and forming for ducts, hoods, hoppers, industrial enclosures and sheet metal tanks.",
    },
    {
        icon: Cog,
        title: "Structural Fabrication",
        desc: "Heavy structurals, platforms, walkways, silos and support frames. Designed, welded and erected on-site.",
    },
    {
        icon: Boxes,
        title: "Process Pipelines & Installation",
        desc: "Process piping, utility piping, oil & fats plant lines, mechanical installations and complete site commissioning.",
    },
    {
        icon: HardHat,
        title: "Erection of Equipments",
        desc: "Heavy equipment erection at plant sites. Cranes, hydra and rigging teams for boilers, vessels and process machinery.",
    },
];

const CLIENTS = [
    "Adani Wilmar Ltd.",
    "Cargill",
    "Bunge India",
    "Louis Dreyfus Company",
    "Grasim Industries",
    "Emami Agro-Tech",
    "Gemini Edibles & Fats",
    "Acalmar Oil & Fats",
    "KTV Health Foods",
    "Anagha Refineries",
    "Mepco",
    "ARC Petro Chemicals",
    "V.B. Oil Mills",
    "Abhaycotex Pvt Ltd",
];

const GALLERY = [
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_web-forge-1752/artifacts/84o5p9r5_image.png",
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_web-forge-1752/artifacts/h55iswp6_image.png",
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_web-forge-1752/artifacts/mged5z9y_image.png",
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_web-forge-1752/artifacts/v83phcdu_image.png",
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_web-forge-1752/artifacts/z31ikmxd_WhatsApp%20Image%202026-07-21%20at%2021.33.11.jpeg",
    "https://customer-assets-lxgj4vgw.emergentagent.net/job_web-forge-1752/artifacts/jk9ivs2h_WhatsApp%20Image%202026-07-21%20at%2021.33.16.jpeg",
];

// ============================================================
// Header
// ============================================================
const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, [setScrolled]);

    const links = [
        { href: "#about", label: "About" },
        { href: "#services", label: "Services" },
        { href: "#gallery", label: "Our Work" },
        { href: "#clients", label: "Clients" },
        { href: "#contact", label: "Contact" },
    ];

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-[var(--navy-900)]/95 backdrop-blur-md border-b border-white/5"
                    : "bg-transparent"
            }`}
        >
            <div className="container-x flex items-center justify-between h-20">
                <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
                    <img
                        src={BRAND.logo}
                        alt="Jesha Engineering"
                        className="h-11 w-11 object-contain bg-white p-1 rounded"
                    />
                    <div className="leading-tight">
                        <div className="font-display text-white text-lg tracking-tight">
                            JESHA
                        </div>
                        <div className="font-mono-b text-[0.62rem] text-[var(--silver-300)] tracking-[0.24em] uppercase">
                            Engineering Co.
                        </div>
                    </div>
                </a>

                <nav className="hidden md:flex items-center gap-8">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className="font-display text-white/90 hover:text-white text-sm transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-5">
                    <a
                        href={`mailto:${BRAND.email}`}
                        data-testid="header-email"
                        className="font-mono-b text-white text-sm tracking-wider hover:text-[var(--silver-300)] transition-colors flex items-center gap-2"
                    >
                        <Mail size={14} />
                        {BRAND.email}
                    </a>
                    <a
                        href={`tel:${BRAND.phoneRaw}`}
                        data-testid="header-phone"
                        className="font-mono-b text-white text-sm tracking-wider hover:text-[var(--silver-300)] transition-colors flex items-center gap-2"
                    >
                        <Phone size={14} />
                        {BRAND.phone}
                    </a>
                </div>

                <button
                    className="md:hidden text-white"
                    onClick={() => setOpen((s) => !s)}
                    data-testid="mobile-menu-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={26} /> : <Menu size={26} />}
                </button>
            </div>

            {open && (
                <div className="md:hidden bg-[var(--navy-900)] border-t border-white/10">
                    <div className="container-x py-4 flex flex-col gap-4">
                        {links.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                className="font-display text-white"
                                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                            >
                                {l.label}
                            </a>
                        ))}
                        <a href={`tel:${BRAND.phoneRaw}`} className="btn-primary w-fit" onClick={() => setOpen(false)} style={{background:"#fff",color:"var(--navy-800)",borderColor:"#fff"}}>
                            <Phone size={16} /> {BRAND.phone}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};

// ============================================================
// Hero
// ============================================================
const Hero = () => (
    <section id="top" className="relative bg-blueprint pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[var(--navy-500)]/10 blur-3xl" />

        <div className="container-x relative">
            <div className="grid md:grid-cols-12 gap-10 items-center">
                <div className="md:col-span-8">
                    <div className="rise-in">
                        <span className="eyebrow">Est. 25+ Years · South India</span>
                    </div>
                    <h1
                        data-testid="hero-title"
                        className="rise-in delay-1 font-display text-white text-[clamp(2.6rem,7vw,5.6rem)] mt-6"
                    >
                        Boilers Built.<br />
                        Pipelines Laid.<br />
                        <span className="text-[var(--silver-300)]">Fabrication Delivered.</span>
                    </h1>

                    <p
                        data-testid="hero-subtitle"
                        className="rise-in delay-2 text-[var(--silver-200)] text-lg md:text-xl mt-8 max-w-2xl leading-relaxed"
                    >
                        Licensed boiler contractors and heavy fabrication specialists trusted by
                        Adani Wilmar, Cargill, Bunge and Louis Dreyfus, with a workforce
                        of 360+ engineers, welders and fitters.
                    </p>

                    <div className="rise-in delay-3 mt-8 flex flex-wrap gap-3">
                        <span className="credential-badge" data-testid="badge-ap-license">
                            <span className="dot" />
                            AP Boiler Licence · Reg. No. 84/2008
                        </span>
                        <span className="credential-badge">
                            <span className="dot" />
                            25+ Years Experience
                        </span>
                        <span className="credential-badge">
                            <span className="dot" />
                            360+ Skilled Crew
                        </span>
                    </div>

                    <div className="rise-in delay-4 mt-10 flex flex-wrap items-center gap-4">
                        <a href={`tel:${BRAND.phoneRaw}`} data-testid="hero-cta-call" className="btn-primary" style={{background:"#fff",color:"var(--navy-800)",borderColor:"#fff"}}>
                            <Phone size={18} /> {BRAND.phone}
                        </a>
                        <a href={`mailto:${BRAND.email}`} data-testid="hero-cta-email" className="btn-outline">
                            <Mail size={18} /> {BRAND.email}
                        </a>
                    </div>
                </div>

                {/* Right — technical panel */}
                <div className="md:col-span-4 hidden md:block">
                    <div className="relative rise-in delay-2">
                        <div className="absolute -top-3 -left-3 w-24 h-24 border-2 border-[var(--silver-500)]" />
                        <div className="relative bg-[var(--navy-950)] border border-white/10 p-8">
                            <div className="eyebrow">Operations Bulletin</div>
                            <div className="mt-6 space-y-4 font-mono-b text-sm">
                                {[
                                    ["STATUS", "OPERATIONAL"],
                                    ["LICENCE", "AP 84/2008"],
                                    ["COVERAGE", "AP · TN · KA · MH"],
                                    ["CREW", "360 PERSONNEL"],
                                    ["EXPERIENCE", "25+ YEARS"],
                                ].map(([k, v]) => (
                                    <div key={k} className="flex justify-between items-center border-b border-white/10 pb-3">
                                        <span className="text-[var(--silver-400)] tracking-widest">{k}</span>
                                        <span className="text-white font-bold">{v}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 flex items-center gap-2 text-[var(--success)]">
                                <span className="inline-block w-2 h-2 rounded-full bg-[var(--success)] animate-pulse" />
                                <span className="font-mono-b text-xs tracking-widest">READY FOR DEPLOYMENT</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

// ============================================================
// About
// ============================================================
const About = () => (
    <section id="about" className="py-24 md:py-32 bg-[var(--paper)]">
        <div className="container-x grid md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
                <div className="eyebrow" style={{color:"var(--navy-600)"}}>About the Company</div>
                <h2 data-testid="about-heading" className="font-display text-[var(--navy-900)] text-5xl md:text-6xl mt-4">
                    A Quarter&#8209;Century of<br />
                    <span className="text-[var(--silver-500)]">Industrial Precision.</span>
                </h2>
            </div>
            <div className="md:col-span-7 text-[var(--navy-800)] text-lg leading-relaxed space-y-6">
                <p>
                    <strong className="text-[var(--navy-900)]">Jesha Engineering Company</strong> is a pioneering
                    industrial construction firm headquartered in Nellore, Andhra Pradesh, serving
                    South Indian states for over 25 years. As an AP boiler licence holder
                    (<strong>Reg. No. 84/2008</strong>), we specialise in boiler installation, boiler
                    repair, steam and utility pipeline erection, sheet metal fabrication, structural
                    fabrication, process pipelines and mechanical installation jobs.
                </p>
                <p>
                    We keep pace with cutting&#8209;edge technologies to help our customers build better
                    products for tomorrow. Quality assurance at every level ensures reliability, backed
                    by an extensive service network and spare&#8209;parts inventory ready to attend to any
                    customer on&#8209;site demand.
                </p>

                <div className="grid sm:grid-cols-3 gap-4 pt-4">
                    {[
                        { i: Award, t: "AP Boiler Licence 84/2008" },
                        { i: Users, t: "360+ Trained Personnel" },
                        { i: ShieldCheck, t: "Zero-Compromise Quality" },
                    ].map((c) => (
                        <div key={c.t} className="flex items-start gap-3">
                            <div className="mt-1 w-9 h-9 flex items-center justify-center bg-[var(--navy-800)] text-white shrink-0 rounded">
                                <c.i size={18} />
                            </div>
                            <div className="text-[var(--navy-900)] font-semibold">{c.t}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </section>
);

// ============================================================
// Services
// ============================================================
const Services = () => (
    <section id="services" className="py-24 md:py-32 bg-[var(--navy-900)] relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint opacity-40" />
        <div className="container-x relative">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                <div>
                    <div className="eyebrow">What We Do</div>
                    <h2 data-testid="services-heading" className="font-display text-white text-5xl md:text-6xl mt-4">
                        Seven Core Services.<br />
                        <span className="text-[var(--silver-300)]">One Trusted Contractor.</span>
                    </h2>
                </div>
                <p className="text-[var(--silver-300)] max-w-md text-lg leading-relaxed">
                    From single&#8209;drum boiler erection to plant&#8209;scale pipeline networks, we handle
                    every stage in&#8209;house, on schedule and to statutory standards.
                </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {SERVICES.map((s, i) => (
                    <div
                        key={s.title}
                        data-testid={`service-card-${i}`}
                        className="group relative bg-[var(--navy-800)] border border-white/10 p-8 transition-all duration-300 hover:border-[var(--silver-400)] hover:-translate-y-1 rounded"
                    >
                        <div className="absolute top-0 left-0 h-1 w-0 bg-[var(--silver-300)] transition-all duration-500 group-hover:w-full" />
                        <div className="w-14 h-14 flex items-center justify-center bg-[var(--navy-950)] text-white border border-white/10 mb-6 rounded">
                            <s.icon size={26} strokeWidth={1.6} />
                        </div>
                        <div className="font-mono-b text-xs text-[var(--silver-400)] tracking-widest">
                            0{i + 1} / 07
                        </div>
                        <h3 className="font-display text-white text-2xl mt-2 mb-3">{s.title}</h3>
                        <p className="text-[var(--silver-200)] leading-relaxed">{s.desc}</p>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

// ============================================================
// Gallery — Our Work
// ============================================================
const Gallery = () => (
    <section id="gallery" className="py-24 md:py-32 bg-[var(--paper)]">
        <div className="container-x">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                <div>
                    <div className="eyebrow" style={{color:"var(--navy-600)"}}>Our Work</div>
                    <h2 data-testid="gallery-heading" className="font-display text-[var(--navy-900)] text-5xl md:text-6xl mt-4">
                        From the Shop Floor<br />
                        <span className="text-[var(--silver-500)]">to the Plant Site.</span>
                    </h2>
                </div>
                <p className="text-[var(--navy-800)] max-w-md text-lg leading-relaxed">
                    A glimpse of live installations, tank farms, structural erections
                    and utility pipelines executed across South Indian process plants.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gallery-grid">
                {GALLERY.map((src, i) => (
                    <figure
                        key={src}
                        data-testid={`gallery-item-${i}`}
                        className="group relative overflow-hidden bg-[var(--navy-900)] rounded aspect-[4/5]"
                    >
                        <img
                            src={src}
                            alt={`Jesha Engineering project ${i + 1}`}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                    </figure>
                ))}
            </div>
        </div>
    </section>
);

// ============================================================
// Clients
// ============================================================
const Clients = () => {
    const doubled = [...CLIENTS, ...CLIENTS];
    return (
        <section id="clients" className="py-24 md:py-28 bg-[var(--navy-950)] relative overflow-hidden">
            <div className="container-x relative mb-10">
                <div className="text-center">
                    <div className="eyebrow" style={{color:"var(--silver-400)"}}>Major Clients</div>
                </div>
            </div>

            <div className="relative py-6" data-testid="clients-ticker">
                <div className="ticker-track gap-4 px-4">
                    {doubled.map((c, i) => (
                        <div
                            key={`${c}-${i}`}
                            className="shrink-0 min-w-max px-8 py-5 border border-white/10 bg-[var(--navy-900)] rounded"
                        >
                            <div className="font-display text-white text-2xl tracking-tight whitespace-nowrap">
                                {c}
                            </div>
                        </div>
                    ))}
                </div>

                {/* fades */}
                <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[var(--navy-950)] to-transparent" />
                <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[var(--navy-950)] to-transparent" />
            </div>

            <div className="container-x mt-14 grid md:grid-cols-2 gap-8 text-center">
                {[
                    { k: "5", v: "States Covered" },
                    { k: "25+", v: "Years of Trust" },
                ].map((s) => (
                    <div key={s.v} className="border-t border-white/10 pt-6">
                        <div className="font-display text-white text-5xl">{s.k}</div>
                        <div className="font-mono-b text-[var(--silver-400)] text-xs tracking-widest uppercase mt-2">
                            {s.v}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

// ============================================================
// Contact — info only, no form
// ============================================================
const Contact = () => (
    <section id="contact" className="py-24 md:py-32 bg-[var(--paper)]">
        <div className="container-x">
            <div className="text-center max-w-3xl mx-auto mb-14">
                <div className="eyebrow" style={{color:"var(--navy-600)"}}>Get in Touch</div>
                <h2 data-testid="contact-heading" className="font-display text-[var(--navy-900)] text-5xl md:text-6xl mt-4">
                    Call. Mail. Visit.<br />
                    <span className="text-[var(--silver-500)]">We&apos;re on the Line.</span>
                </h2>
                <p className="text-[var(--navy-800)] text-lg mt-6 leading-relaxed">
                    Reach us directly for site inspections, quotations or emergency boiler repair.
                    Our engineers respond the same working day.
                </p>
            </div>

            <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
                <a
                    href={`tel:${BRAND.phoneRaw}`}
                    data-testid="contact-phone"
                    className="group card-steel p-8 rounded flex flex-col items-start"
                >
                    <div className="w-12 h-12 bg-[var(--navy-800)] text-white flex items-center justify-center rounded mb-5 group-hover:bg-[var(--navy-950)] transition-colors">
                        <Phone size={22} />
                    </div>
                    <div className="font-mono-b text-xs uppercase tracking-widest text-[var(--silver-500)]">
                        Call Direct
                    </div>
                    <div className="font-display text-2xl md:text-3xl text-[var(--navy-900)] mt-2 group-hover:text-[var(--navy-600)] transition-colors">
                        {BRAND.phone}
                    </div>
                    <div className="mt-4 font-mono-b text-[0.7rem] tracking-widest uppercase text-[var(--silver-500)] flex items-center gap-1">
                        Tap to Call <ArrowRight size={12} />
                    </div>
                </a>

                <a
                    href={`mailto:${BRAND.email}`}
                    data-testid="contact-email"
                    className="group card-steel p-8 rounded flex flex-col items-start"
                >
                    <div className="w-12 h-12 bg-[var(--navy-800)] text-white flex items-center justify-center rounded mb-5 group-hover:bg-[var(--navy-950)] transition-colors">
                        <Mail size={22} />
                    </div>
                    <div className="font-mono-b text-xs uppercase tracking-widest text-[var(--silver-500)]">
                        Email
                    </div>
                    <div className="font-display text-xl md:text-2xl text-[var(--navy-900)] mt-2 group-hover:text-[var(--navy-600)] transition-colors break-all">
                        {BRAND.email}
                    </div>
                    <div className="mt-4 font-mono-b text-[0.7rem] tracking-widest uppercase text-[var(--silver-500)] flex items-center gap-1">
                        Send a Mail <ArrowRight size={12} />
                    </div>
                </a>

                <div className="card-steel p-8 rounded flex flex-col items-start" data-testid="contact-address">
                    <div className="w-12 h-12 bg-[var(--navy-800)] text-white flex items-center justify-center rounded mb-5">
                        <MapPin size={22} />
                    </div>
                    <div className="font-mono-b text-xs uppercase tracking-widest text-[var(--silver-500)]">
                        Head Office
                    </div>
                    <div className="font-display text-xl md:text-2xl text-[var(--navy-900)] mt-2">
                        {BRAND.address}
                    </div>
                    <div className="mt-4 font-mono-b text-[0.7rem] tracking-widest uppercase text-[var(--silver-500)]">
                        Serving AP · TN · KA · MH · TS
                    </div>
                </div>
            </div>

            <div className="mt-14 text-center">
                <span className="credential-badge" style={{background:"var(--navy-800)",borderColor:"var(--navy-800)"}}>
                    <span className="dot" />
                    {BRAND.license}
                </span>
            </div>
        </div>
    </section>
);

// ============================================================
// Footer
// ============================================================
const Footer = () => (
    <footer className="bg-[var(--navy-950)] text-[var(--silver-200)] pt-16 pb-8">
        <div className="container-x">
            <div className="grid md:grid-cols-4 gap-10">
                <div className="md:col-span-2">
                    <div className="flex items-center gap-3">
                        <img
                            src={BRAND.logo}
                            alt="Jesha Engineering"
                            className="h-12 w-12 object-contain bg-white p-1 rounded"
                        />
                        <div>
                            <div className="font-display text-white text-2xl">JESHA</div>
                            <div className="font-mono-b text-[var(--silver-300)] text-[0.68rem] tracking-[0.24em] uppercase">
                                Engineering Company
                            </div>
                        </div>
                    </div>
                    <p className="mt-6 max-w-md text-sm leading-relaxed text-[var(--silver-300)]">
                        Licensed boiler contractors and heavy fabrication specialists serving
                        South India for 25+ years. Boiler installation, repair, steam pipelines,
                        sheet metal and structural fabrication end to end.
                    </p>
                </div>

                <div>
                    <div className="font-display text-white text-lg mb-4">Explore</div>
                    <ul className="space-y-2 text-sm">
                        {["About", "Services", "Gallery", "Clients", "Contact"].map((l) => (
                            <li key={l}>
                                <a
                                    href={`#${l.toLowerCase() === "gallery" ? "gallery" : l.toLowerCase()}`}
                                    className="hover:text-white transition-colors"
                                >
                                    {l}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <div className="font-display text-white text-lg mb-4">Reach Us</div>
                    <ul className="space-y-3 text-sm">
                        <li className="flex items-start gap-2">
                            <Phone size={14} className="mt-1 text-[var(--silver-300)]" />
                            <a href={`tel:${BRAND.phoneRaw}`} className="hover:text-white">
                                {BRAND.phone}
                            </a>
                        </li>
                        <li className="flex items-start gap-2">
                            <Mail size={14} className="mt-1 text-[var(--silver-300)]" />
                            <a href={`mailto:${BRAND.email}`} className="hover:text-white break-all">
                                {BRAND.email}
                            </a>
                        </li>
                        <li className="flex items-start gap-2">
                            <MapPin size={14} className="mt-1 text-[var(--silver-300)]" />
                            <span>{BRAND.address}</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3">
                <div className="text-xs text-[var(--silver-500)] font-mono-b tracking-widest uppercase">
                    © {new Date().getFullYear()} Jesha Engineering Company. All Rights Reserved
                </div>
                <div className="text-xs text-[var(--silver-500)] font-mono-b tracking-widest uppercase">
                    AP Boiler Licence Reg. No. 84/2008
                </div>
            </div>
        </div>
    </footer>
);

// ============================================================
// App
// ============================================================
function App() {
    return (
        <div className="App">
            <Header />
            <main>
                <Hero />
                <About />
                <Services />
                <Gallery />
                <Clients />
                <Contact />
            </main>
            <Footer />
        </div>
    );
}

export default App;
